import { Dispatch, SetStateAction } from 'react'
import {
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { useTranslation } from 'react-i18next'

import { TableBodyRow } from './table-body-row.component'
import { TrashTimeoutIcon } from 'src/assets/icons'
import {
  useCancelUsersRemove,
  useNotification,
  useRemoveUsers,
  useTableItemsCheck,
} from 'src/hooks'
import { ETranslationNs, IProfileUserResponse } from 'src/types'
import { IFilter } from 'src/components/filter/types/filter.types'
import { HelperWrapper } from 'src/components/helper-wrapper'

interface TableViewProps {
  data: IProfileUserResponse[] | undefined | void
  filter: IFilter
  sort: string
  setSort: Dispatch<SetStateAction<string>>
}

const TableView = ({ data: users, filter, sort, setSort }: TableViewProps) => {
  const [t] = useTranslation()
  const { mutateAsync: removeUsers } = useRemoveUsers(filter)
  const { mutateAsync: cancelRemove } = useCancelUsersRemove()
  const { notify } = useNotification()
  const theme = useTheme()

  const {
    handleAllItemsCheck,
    selectedItemsIds,
    isItemSelected,
    handleItemCheck,
    setSelectedItemsIds,
    isAllUnchecked,
    isAllChecked,
    isIndeterminated,
  } = useTableItemsCheck({
    items: users ?? [],
  })

  const handleItemsRemove = async () => {
    if (!selectedItemsIds.length) return

    const resp = await removeUsers(selectedItemsIds)
    if (!resp) return setSelectedItemsIds([])

    notify({
      message: t('users-delete-notification.v', { ns: ETranslationNs.Common }),
      icon: <TrashTimeoutIcon />,
      onButtonClick: () => {
        cancelRemove({ ids: selectedItemsIds })
      },
      timeout: 14000,
    })
    setSelectedItemsIds([])
  }

  const handleSortChange = (sortName: string) => () => {
    setSort((prevSort) =>
      prevSort.split('-')[1] === 'asc' ? `${sortName}-desc` : `${sortName}-asc`,
    )
  }

  const sortDirection = sort.split('-')[1] as 'desc' | 'asc'
  const sortName = sort.split('-')[0]
  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow style={{ border: 0 }} sx={{ 'td, th': { border: 0, p: '16px 2px' } }}>
              <TableCell align="center">
                <Checkbox
                  indeterminate={isIndeterminated}
                  sx={{ color: theme.palette.custom.grey2 }}
                  onChange={handleAllItemsCheck}
                  checked={isAllChecked}
                />
              </TableCell>
              <TableCell align="center" />
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('email')}
                  active={sortName === 'email'}
                  direction={sortDirection}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="user-table-email-column" ns={ETranslationNs.Users} />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <HelperWrapper t="accounts-header-title" ns={ETranslationNs.Navbar} />
              </TableCell>
              <TableCell align="center">
                Trackgood <HelperWrapper t="table-role-column" ns={ETranslationNs.Users} />
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('activated')}
                  active={sortName === 'activated'}
                  direction={sortDirection}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="table-activated-column" ns={ETranslationNs.Users} />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('agreement')}
                  active={sortName === 'agreement'}
                  direction={sortDirection}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="table-agreement-column" ns={ETranslationNs.Users} />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('created_at')}
                  active={sortName === 'created_at'}
                  direction={sortDirection}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="table-created-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <TableSortLabel
                  onClick={handleSortChange('updated_at')}
                  active={sortName === 'updated_at'}
                  direction={sortDirection}
                  sx={{
                    marginLeft: '20px',
                  }}
                >
                  <HelperWrapper t="table-modified-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">
                <IconButton disabled={isAllUnchecked} onClick={handleItemsRemove}>
                  <DeleteIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users?.map((row) => (
              <TableBodyRow
                key={row.id}
                handleItemCheck={handleItemCheck}
                isItemSelected={isItemSelected}
                row={row}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}

export { TableView }
